import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  Modal,
  Pagination,
  Paper,
  RadioGroup,
  Stack,
  TextField,
  TextareaAutosize,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useToast } from 'use-toast-mui';
import { pageSize } from '../../constants/constants';
import { FormInputDate } from '../../form-components/FormInputDate';
import { FormInputText } from '../../form-components/FormInputText';
import AnimationPage from '../../pages/AnimationPage';
import { errorMessages } from '../../utils/methods';
import CustomCheckbox from '../CustomCheckbox';
import FilterButtons from '../FilterButtons';
import { FormInputDropdown } from '../form/FormInputDropdown';
import Loader from '../loader';
import MembersListTable from './MembersListTable';
import * as XLSX from 'xlsx';
import { getUnsubscribedUsersList } from '../../api/member-management';
import { CommonExcelButton } from '../../form-components/CommonExcelButton';

const initialQuery = {
  page: 1,
  limit: 10,
  status: '',
  searchInput: '',
  registeredDateFrom: '',
  registeredDateTo: '',
  genders: [],
  ageRanges: [],
};

var qs = require('qs');

const gender = ['MALE', 'FEMALE'];
const ageRange = ['UNDER_30', 'AGE_30_40', 'AGE_40_50', 'AGE_50_60', 'OVER_60'];

const MembersList = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const token = useSelector(function (state) {
    return state.user.user.token;
  });
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [managers, setManagers] = useState();
  const [query, setQuery] = useState(initialQuery);

  const { handleSubmit, control, reset, setValue, watch } = useForm({ defaultValues: initialQuery });

  const handleChangePage = (_, value) => {
    setQuery({ ...query, page: value });
    getManagerList({ ...query, page: value });
  };

  const handleChangeLimit = e => {
    setQuery({ ...query, limit: e.target.value, page: 1 });
    getManagerList({ ...query, limit: e.target.value, page: 1 });
  };

  useEffect(() => {
    getManagerList(initialQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getManagerList = async queries => {
    setQuery({ ...query, ...queries });
    const queryString = qs.stringify({ ...query, ...queries });
    try {
      setLoading(true);
      await getUnsubscribedUsersList(token, queryString).then(result => {
        setLoading(false);
        setManagers(result);
      });
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  const submit = async values => {
    const from = new Date(values.registeredDateFrom);
    const to = new Date(values.registeredDateTo);
    const registeredDateFrom = values.registeredDateFrom ? from.toISOString().split('T')[0] : '';
    const registeredDateTo = values.registeredDateTo ? to.toISOString().split('T')[0] : '';
    getManagerList({
      ...query,
      searchInput: values.searchInput,
      status: query.status,
      registeredDateFrom: registeredDateFrom,
      registeredDateTo: registeredDateTo,
    });
  };

  const setInitial = () => {
    setQuery(initialQuery);
    reset(initialQuery);

    getManagerList(initialQuery);
  };

  const handleGender = gender => {
    if (query.genders.includes(gender)) {
      setQuery({
        ...query,
        genders: query.genders.filter(s => s !== gender),
      });
    } else {
      setQuery({
        ...query,
        genders: [...query.genders, gender],
      });
    }
  };
  const handleAgeRange = range => {
    if (query.ageRanges.includes(range)) {
      setQuery({
        ...query,
        ageRanges: query.ageRanges.filter(s => s !== range),
      });
    } else {
      setQuery({
        ...query,
        ageRanges: [...query.ageRanges, range],
      });
    }
  };

  const handleChange = e => {
    const { name, checked } = e.target;
    var tempUser = {};
    if (name === 'allSelect') {
      tempUser = managers?.content?.map(user => {
        return { ...user, isChecked: checked };
      });
    } else {
      tempUser = managers?.content?.map(user =>
        user.username + user.memberId === name ? { ...user, isChecked: checked } : user,
      );
    }
    setManagers({ ...managers, content: tempUser });
  };

  const exportToExcel = () => {
    const filteredData = managers?.content
      ?.filter(user => user.isChecked)
      ?.map(({ username, memberId, phoneNumber, firstPaymentDate, age, activeTickets }) => ({
        이름: username,
        전화번호: phoneNumber,
        '첫 결제일': firstPaymentDate,
        나이: age,
        // '	사용 수강권': activeTickets.map(ticket => ticket.ticketName).join(', '),
      }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(filteredData);
    XLSX.utils.book_append_sheet(wb, ws, 'Users');
    XLSX.writeFile(wb, 'data.xlsx');
  };
  return (
    <AnimationPage>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography className="title" sx={{ width: '97px', fontSize: '13px', fontWeight: 500 }}>
              {t('Search')}
            </Typography>
            <FormInputText
              control={control}
              name="searchInput"
              sx={{ width: '348px', borderRadius: '8px' }}
              placeholder={t('내용을 입력해주세요')}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  handleSubmit(submit)();
                }
              }}
            />
            <Button
              sx={{
                borderRadius: '5px',
                minWidth: 60,
                height: '38px',
                ml: '4px',
                border: '1px solid #0F7F85',
                background: '#0F7F85',
                '&:hover': {
                  background: 'none',
                  color: '#0F7F85',
                },
              }}
              variant="contained"
              color="info"
              onClick={handleSubmit(submit)}>
              {t('Search')}
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', mt: '27px', gap: '40px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Typography className="title" sx={{ width: '81px' }}>
              {t('Gender')}
            </Typography>
            <FormControl>
              <RadioGroup row>
                {gender.map((type, index) => {
                  return (
                    <FormControlLabel
                      control={
                        <CustomCheckbox
                          checked={query.genders.indexOf(type) !== -1}
                          onChange={() => handleGender(type)}
                        />
                      }
                      label={t(type)}
                    />
                  );
                })}
              </RadioGroup>
            </FormControl>
          </Box>
          <Box sx={{ height: '20px', width: '1px', background: '#ddd', ml: '0px', mr: '0px' }}></Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mr: '75px' }}>
            <Typography className="title" sx={{ width: '81px', marginLeft: '20px' }}>
              {t('Age Group')}
            </Typography>
            <FormControl>
              <RadioGroup row>
                {ageRange.map((type, index) => {
                  return (
                    <FormControlLabel
                      control={
                        <CustomCheckbox
                          checked={query.ageRanges.indexOf(type) !== -1}
                          onChange={() => handleAgeRange(type)}
                        />
                      }
                      label={t(type)}
                    />
                  );
                })}
              </RadioGroup>
            </FormControl>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', mt: 4 }}>
          <Typography sx={{ width: '97px', fontSize: '13px', fontWeight: 500 }}>{t('Period')}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', columnGap: '20px' }}>
            <FormInputDate
              name="registeredDateFrom"
              control={control}
              placeholder={t('Select date')}
              sx={{
                '.css-1x51dt5-MuiInputBase-input-MuiInput-input': {
                  fontSize: '16px',
                },
              }}
              renderInput={params => (
                <TextField
                  color="info"
                  size="small"
                  variant="outlined"
                  {...params}
                  sx={{
                    width: '140px',
                    background: '#fff',
                    '.MuiInputBase-root': {
                      paddingRight: '0px',
                    },
                    '.MuiIconButton-root': {
                      marginRight: '0px',
                    },
                    input: {
                      paddingRight: '0px!important',
                    },
                    '& .MuiSvgIcon-root': {
                      fill: '#0F7F85 !important',
                    },
                  }}
                />
              )}
            />
            <img src="/images/icons/temp.svg" alt="temp" style={{ width: 10, height: 16 }} />
            <FormInputDate
              name="registeredDateTo"
              control={control}
              minDate={watch('registeredDateFrom') || null}
              placeholder={t('Select date')}
              sx={{
                '.css-1x51dt5-MuiInputBase-input-MuiInput-input': {
                  fontSize: '16px',
                },
              }}
              renderInput={params => (
                <TextField
                  color="info"
                  size="small"
                  variant="outlined"
                  {...params}
                  sx={{
                    width: '140px',
                    background: '#fff',
                    '.MuiInputBase-root': {
                      paddingRight: '0px',
                    },
                    '.MuiIconButton-root': {
                      marginRight: '0px',
                    },
                    input: {
                      paddingRight: '0px!important',
                    },
                    '& .MuiSvgIcon-root': {
                      fill: '#0F7F85 !important',
                    },
                  }}
                />
              )}
            />

            <FilterButtons setValue={setValue} watch={watch} />
          </Box>
        </Box>

        <Divider sx={{ border: '1px solid #e1e1e1', my: 2 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Button
            variant="contained"
            sx={{
              borderRadius: '5px',
              minWidth: 220,
              height: 40,
              border: '1px solid #0F7F85',
              background: '#0F7F85',
              '&:hover': {
                background: 'none',
                color: '#0F7F85',
              },
            }}
            onClick={handleSubmit(submit)}>
            {t('Search for members with set conditions')}
          </Button>
          <Button
            onClick={setInitial}
            variant="contained"
            color="inherit"
            sx={{
              minWidth: 140,
              height: 40,
              borderRadius: '5px',
              ml: 1,
              background: '#e6e6e6',
              border: '1px solid #e6e6e6',
              '&:hover': {
                background: 'none',
              },
            }}>
            {t('Reset search conditions')}
          </Button>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {/* <Button
              onClick={() => setOpen(true)}
              variant="contained"
              color="secondary"
              sx={{ borderRadius: '12px', mr: '4px', width: 60, height: 40 }}>
              SMS
            </Button> */}
            <CommonExcelButton
              onClick={exportToExcel}
              disabled={loading || managers?.content?.every(user => !user.isChecked)}>
              {t('Download Excel File')}
            </CommonExcelButton>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mt: 3,
              mb: 1,
            }}>
            <FormInputDropdown
              control={control}
              name="limit"
              options={pageSize}
              staticValue={query.limit}
              onChange={handleChangeLimit}
              sx={{ width: '108px', borderRadius: '5px !important' }}
            />
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Loader style={{ backgroundColor: 'red' }} />
          </Box>
        ) : (
          <Box sx={{ width: '100%' }}>
            <MembersListTable managers={managers} handleChange={handleChange} />

            <Stack spacing={1} sx={{ mt: '20px', ml: '40%' }}>
              <Pagination
                count={Math.ceil(managers?.totalElements / query.limit) || 0}
                onChange={handleChangePage}
                page={query.page}
                color="primary"
                variant="outlined"
                shape="rounded"
                sx={{
                  '& .MuiPaginationItem-root.MuiPaginationItem-sizeMedium': {
                    background: '#0F7F85',
                    border: 'none',
                    height: '24px',
                    minWidth: '24px',
                  },
                  '& .MuiPaginationItem-root.MuiPaginationItem-sizeMedium.Mui-selected': {
                    background: '#0F7F85',
                    color: '#fff',
                  },
                  '.MuiPaginationItem-icon': {
                    color: '#fff',
                  },
                }}
              />
            </Stack>
          </Box>
        )}
      </Box>

      {/* modal starts */}
      <Modal
        open={open}
        // onClose={handleClose}
        aria-labelledby="child-modal-title"
        aria-describedby="child-modal-description">
        <div
          style={{
            backgroundColor: '#fff',
            borderRadius: '10px',
            width: '606px',
            height: '580px',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              color: '#fff',
              background: '#202020',
              height: '52px',
              borderRadius: '10px 10px 0 0',
              p: '0px 20px 0px 28px',
            }}>
            <Typography className="title" sx={{ fontSize: '16px' }}>
              {t('Send message')}{' '}
            </Typography>
            <CloseIcon onClick={() => setOpen(false)} />
          </Box>
          <Box sx={{ p: '20px 28px' }}>
            <Box
              sx={{
                transform: 'translateX(-10px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'start',
                // width: "606px",
              }}>
              <CustomCheckbox />
              <Typography>{t('Excluding members who do not agree to receive SMS')}</Typography>
            </Box>
            <TextareaAutosize
              aria-label="minimum height"
              minRows={17}
              style={{
                width: '100%',
                marginTop: '10px',
                borderRadius: '4px',
                padding: '15px',
                border: '1px solid #e1e1e1',
              }}
              placeholder={t('Please enter the contents')}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'end',
              alignItems: 'end',
              marginRight: '20px',
              columnGap: '8px',
            }}>
            <Button
              sx={{
                borderRadius: '10px',
                border: 'none',
                background: '#c5c5c5',
                color: '#fff',
                '&:hover': { border: 'none', background: '#c5c5c5' },
              }}
              variant="outlined"
              onClick={() => setOpen(false)}>
              {t('Cancel')}
            </Button>
            <Button sx={{ borderRadius: '10px' }} variant="contained" onClick={() => setOpen(false)}>
              {t('Save')}
            </Button>
          </Box>
        </div>
      </Modal>
    </AnimationPage>
  );
};

export default MembersList;
